import { Link } from "react-router-dom";
import {
  BrainCircuit,
  PanelsTopLeft,
  Workflow,
  Database,
  BarChart3,
  GraduationCap,
  ArrowRight,
  ArrowUpRight,
} from "lucide-react";
import { useLanguage } from "@/i18n/LanguageContext";
import { useSEO } from "@/hooks/useSEO";
import { Reveal } from "@/components/motion/Reveal";
import { MaturityBadge } from "@/components/MaturityBadge";

const ICONS = [BrainCircuit, PanelsTopLeft, Workflow, Database, BarChart3, GraduationCap];

export default function InnovationPage() {
  const { t } = useLanguage();
  useSEO({ title: t.innovation.seo.title, description: t.innovation.seo.description, path: "/innovation" });
  const n = t.innovation;

  return (
    <div data-testid="innovation-page">
      <section className="container-site pt-14 sm:pt-20 lg:pt-28 pb-16">
        <Reveal>
          <p className="eyebrow">{n.eyebrow}</p>
          <h1 className="mt-3 font-display text-4xl sm:text-5xl lg:text-6xl font-extrabold tracking-tight">
            {n.title}
          </h1>
          <p className="mt-4 font-display text-xl sm:text-2xl font-bold text-ochre">{n.subtitle}</p>
          <p className="mt-5 max-w-2xl text-base sm:text-lg text-muted-foreground leading-relaxed">{n.intro}</p>
        </Reveal>
      </section>

      <section className="container-site pb-16 sm:pb-24" aria-labelledby="explore-title">
        <Reveal>
          <h2 id="explore-title" className="font-display text-2xl sm:text-3xl font-bold tracking-tight">
            {n.exploreTitle}
          </h2>
        </Reveal>
        <div className="mt-10 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6">
          {n.explore.map((item, i) => {
            const Icon = ICONS[i];
            return (
              <Reveal key={item.title} delay={i * 0.06}>
                <div className="h-full rounded-xl border border-border bg-card p-6 sm:p-8 transition-all duration-300 hover:-translate-y-1 hover:border-ochre/50">
                  <Icon className="h-6 w-6 text-ochre" aria-hidden="true" />
                  <h3 className="mt-4 font-display text-lg font-bold">{item.title}</h3>
                  <p className="mt-2 text-sm text-muted-foreground leading-relaxed">{item.text}</p>
                </div>
              </Reveal>
            );
          })}
        </div>
      </section>

      <section className="bg-sand-muted/60 border-y border-border" aria-labelledby="method-title">
        <div className="container-site py-16 sm:py-24">
          <Reveal>
            <h2 id="method-title" className="font-display text-2xl sm:text-3xl font-bold tracking-tight">
              {n.methodTitle}
            </h2>
          </Reveal>
          <ol className="mt-10 grid grid-cols-1 sm:grid-cols-3 lg:grid-cols-5 gap-6">
            {n.method.map((step, i) => (
              <Reveal key={step.title} delay={i * 0.07}>
                <li className="relative h-full rounded-xl border border-border bg-card p-6">
                  <span className="font-mono text-xs font-semibold text-ochre">{String(i + 1).padStart(2, "0")}</span>
                  <h3 className="mt-3 font-display text-lg font-bold">{step.title}</h3>
                  <p className="mt-2 text-sm text-muted-foreground leading-relaxed">{step.text}</p>
                </li>
              </Reveal>
            ))}
          </ol>
          <Reveal delay={0.2}>
            <p className="mt-10 max-w-3xl rounded-xl border border-ochre/30 bg-ochre/5 p-5 text-sm sm:text-base text-foreground leading-relaxed">
              {n.aiNote}
            </p>
          </Reveal>
        </div>
      </section>

      <section className="container-site py-16 sm:py-24">
        <Reveal>
          <div className="rounded-2xl bg-ink text-sand p-8 sm:p-12 lg:p-16">
            <div className="flex flex-wrap items-center gap-4">
              <p className="eyebrow">{n.flagship.eyebrow}</p>
              <MaturityBadge status="prototype" onDark />
            </div>
            <h2 className="mt-4 font-display text-2xl sm:text-3xl lg:text-4xl font-extrabold tracking-tight text-white">
              Learnova
            </h2>
            <p className="mt-4 max-w-2xl text-sand/75 leading-relaxed">{n.flagship.text}</p>
            <div className="mt-8 flex flex-wrap gap-4">
              <Link
                to="/applications/learnova"
                data-testid="innovation-cta-learnova"
                className="inline-flex items-center gap-2 rounded-full bg-ochre px-6 py-3 text-sm font-semibold text-ink transition-all duration-200 hover:bg-white hover:shadow-md active:scale-[0.98]"
              >
                {n.flagship.cta}
                <ArrowRight className="h-4 w-4" aria-hidden="true" />
              </Link>
              <Link
                to="/applications"
                data-testid="innovation-cta-portfolio"
                className="inline-flex items-center gap-2 rounded-full border border-sand/30 px-6 py-3 text-sm font-semibold text-sand transition-all duration-200 hover:border-ochre hover:text-ochre active:scale-[0.98]"
              >
                {n.flagship.ctaAll}
                <ArrowUpRight className="h-4 w-4" aria-hidden="true" />
              </Link>
            </div>
          </div>
        </Reveal>
      </section>
    </div>
  );
}
